import { z } from "zod";
import { IsoDateTime, Uuid } from "./common";
import { Country, Language, MeResponse, Role, Theme, TenantDto, Timezone, UiLang, Vertical } from "./identity";

export const SSO_AUDIENCE = "imagora";
export const SSO_MAX_AGE_SECONDS = 300;

/** Claims of the RS256 token a host signs. Registered claims (`iss`, `aud`, `exp`, `iat`) are enforced by the verifier. */
export const SsoClaims = z.object({
  /** Host slug. */
  iss: z.string().min(1),
  /** Host user id. */
  sub: z.string().min(1).max(200),
  /** Single use. */
  jti: z.string().min(8).max(200),
  tenant: z.object({
    ref: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    country: Country,
    languages: z.array(Language).min(1).max(8),
    vertical: Vertical,
    timezone: Timezone.optional(),
  }),
  role: Role,
  name: z.string().min(1).max(200),
  email: z.email().optional(),
  lang: UiLang,
  theme: Theme.optional(),
  /** The tenant belongs to this agency. With `role`, the user also gets that role over all of the agency's tenants. */
  agency: z
    .object({
      ref: z.string().min(1).max(200),
      name: z.string().min(1).max(200),
      role: Role.optional(),
    })
    .optional(),
});
export type SsoClaims = z.infer<typeof SsoClaims>;

export const SsoExchangeRequest = z.object({ token: z.string().min(1).max(16_384) });
export type SsoExchangeRequest = z.infer<typeof SsoExchangeRequest>;

export const SsoExchangeResponse = z.object({
  /** Session JWT. The iframe keeps it in memory and sends it as a Bearer token. */
  token: z.string(),
  expiresAt: IsoDateTime,
  tenant: TenantDto,
  user: z.object({ id: Uuid, name: z.string(), email: z.string().nullable() }),
  role: Role,
  lang: UiLang,
  theme: Theme.nullable(),
  whiteLabel: MeResponse.shape.host.shape.whiteLabel,
  /** Origins that may embed this session and exchange messages with it. The iframe checks its parent against these. */
  allowedOrigins: z.array(z.string()),
});
export type SsoExchangeResponse = z.infer<typeof SsoExchangeResponse>;

/** Stable error codes of the exchange. */
export const SSO_ERRORS = [
  "sso_invalid_token",
  "sso_unknown_host",
  "sso_expired",
  "sso_wrong_audience",
  "sso_replayed",
  "sso_invalid_claims",
  "tenant_suspended",
] as const;
